import { CLASSIC, CLASSICAI, COMFORTAI, IND, INDAI, LIGHT, PAUSCHAL, PAUSCHALAI } from '../../general/master-data'
import { ICruise, IPriceModelCabinCategoryPricings } from '../catalog/interfaces'
import { ICabinFlightPrice, ICabinPrice, IMapCruiseGridResult, IPrice } from './interfaces'

/* eslint-disable no-bitwise */

const VERY_LARGE_NUMBER = 1000000

const emptyPrice: IPrice = {
  cabinPrice: {
    I: 0,
    M: 0,
    B: 0,
    S: 0,
    min: VERY_LARGE_NUMBER,
    displayB: false,
  },
  cabinFlightPrice: {
    I: 0,
    M: 0,
    B: 0,
    S: 0,
  },
  mandatoryFlight: false,
  categories: 0,
}

export const mapCruiseToGrid = (cruise: ICruise) => {
  let comfortaiPrices = emptyPrice
  let premiumaiPrices = emptyPrice
  let premiumPrices = emptyPrice
  let classicaiPrices = emptyPrice
  let classicPrices = emptyPrice
  let pauschalPrices = emptyPrice
  let pauschalaiPrices = emptyPrice
  let lightPrices = emptyPrice

  for (const priceModel of Object.keys(cruise.cabinCategoryPricings)) {
    switch (priceModel) {
      case COMFORTAI:
        comfortaiPrices = findPrice(cruise, COMFORTAI)
        break
      case INDAI:
        premiumaiPrices = findPrice(cruise, INDAI)
        break
      case IND:
        premiumPrices = findPrice(cruise, IND)
        break
      case CLASSICAI:
        classicaiPrices = findPrice(cruise, CLASSICAI)
        break
      case CLASSIC:
        classicPrices = findPrice(cruise, CLASSIC)
        break
      case PAUSCHAL:
        pauschalPrices = findPrice(cruise, PAUSCHAL)
        break
      case PAUSCHALAI:
        pauschalaiPrices = findPrice(cruise, PAUSCHALAI)
        break
      case LIGHT:
        lightPrices = findPrice(cruise, LIGHT)
        break
      default:
        break
    }
  }

  const result: IMapCruiseGridResult = {
    id: cruise.id,
    abfahrt: cruise.departure,
    ankunft: cruise.arrival,
    route: cruise.routeName,
    cruiseAttributes: cruise.cruiseAttributes,
    dauer: cruise.duration,
    schiff: cruise.shipId,
    flüge: cruise.flightAvailabilities,
    comai: comfortaiPrices,
    ind: premiumPrices,
    indai: premiumaiPrices,
    cla: classicPrices,
    claai: classicaiPrices,
    pau: pauschalPrices,
    pauai: pauschalaiPrices,
    lig: lightPrices,
    displayB: 'B',
    hasFlexFlight: cruise.hasFlexFlight,
    campaigns: cruise.campaigns,
    amenities: cruise.amenities,
  }

  result.displayB = calcDisplayB(result)

  return result
}

const findPrice = (cruise: ICruise, priceModelKey: string) => {
  const cabinPrice: ICabinPrice = findPricings(cruise.cabinCategoryPricings, cruise.shipId, priceModelKey)
  const flightPrice: ICabinPrice = findPricings(cruise.flightPricings, cruise.shipId, priceModelKey)

  if (flightPrice.min === VERY_LARGE_NUMBER) {
    flightPrice.min = 0
  }

  const cabinFlightPrice: ICabinFlightPrice = {
    I: flightPrice.I > 0 ? cabinPrice.I + flightPrice.I : 0,
    M: flightPrice.M > 0 ? cabinPrice.M + flightPrice.M : 0,
    B: flightPrice.B > 0 ? cabinPrice.B + flightPrice.B : 0,
    S: flightPrice.S > 0 ? cabinPrice.S + flightPrice.S : 0,
  }

  const mandatoryFlight: boolean =
  cruise.cabinCategoryPricings?.[priceModelKey as keyof ICruise].isMandatoryFlight ?? false
  const isMandatoryFlight = [CLASSIC, CLASSICAI, COMFORTAI, IND, INDAI].includes(priceModelKey) && mandatoryFlight

  if (isMandatoryFlight) {
    cabinPrice.I = 0
    cabinPrice.M = 0
    cabinPrice.B = 0
    cabinPrice.S = 0
  }

  let smallestPrice = cabinPrice.I || VERY_LARGE_NUMBER
  smallestPrice = Math.min(smallestPrice, cabinPrice.M || VERY_LARGE_NUMBER)
  smallestPrice = Math.min(smallestPrice, cabinPrice.B || VERY_LARGE_NUMBER)
  smallestPrice = Math.min(smallestPrice, cabinPrice.S || VERY_LARGE_NUMBER)
  cabinPrice.min = smallestPrice

  let categories = 0

  if (cabinPrice.I || cabinFlightPrice.I) {
    categories = categories | (1 << 0)
  }
  if (cabinPrice.M || cabinFlightPrice.M) {
    categories = categories | (1 << 1)
  }
  if (cabinPrice.B || cabinFlightPrice.B) {
    categories = categories | (1 << 2)
  }
  if (cabinPrice.S || cabinFlightPrice.S) {
    categories = categories | (1 << 3)
  }

  const price: IPrice = { cabinPrice, cabinFlightPrice, mandatoryFlight, categories }

  return price
}

const balconyCategories = ['B', 'V', 'K', 'D', 'P', 'L']

const findPricings = (categoryPricings: IPriceModelCabinCategoryPricings, shipId: string, priceModel: string) => {
  const categoriesForPriceModel = categoryPricings[priceModel as keyof IPriceModelCabinCategoryPricings]
  const result: ICabinPrice = {
    I: 0,
    M: 0,
    B: 0,
    S: 0,
    min: VERY_LARGE_NUMBER,
    displayB: false,
  }

  if (!!categoriesForPriceModel?.pricings?.I) {
    result.I = categoriesForPriceModel.pricings.I
  }

  if (!!categoriesForPriceModel?.pricings?.M) {
    result.M = categoriesForPriceModel.pricings.M
  }

  {
    let minPrice = VERY_LARGE_NUMBER
    let minCategory: string | null = null

    for (const category of balconyCategories) {
      const price = categoriesForPriceModel?.pricings?.[category]

      if (!!price && price < minPrice) {
        minPrice = price
        minCategory = category
      }
    }

    if (!!minCategory) {
      result.B = minPrice

      if ((shipId === 'NO' || shipId === 'CO') && minCategory === 'B') {
        result.displayB = true
      }
    }
  }

  {
    let minPrice = VERY_LARGE_NUMBER

    if (!!categoriesForPriceModel?.pricings?.S) {
      minPrice = categoriesForPriceModel.pricings.S
    }

    if (!!categoriesForPriceModel?.pricings?.J) {
      const price = categoriesForPriceModel.pricings.J

      if (price < minPrice) {
        minPrice = price
      }
    }

    if (minPrice < VERY_LARGE_NUMBER) {
      result.S = minPrice
    }
  }

  return result
}

const calcDisplayB = (result: IMapCruiseGridResult) => {
  switch(result.schiff) {
    case 'NO':
    case 'CO':
      // TODO: check why not claai and indai (and comai + pauai?)
      const noBalcony = !(
        result.ind.cabinPrice.displayB ||
        result.cla.cabinPrice.displayB ||
        result.pau.cabinPrice.displayB ||
        result.lig.cabinPrice.displayB
      )

      if (noBalcony) {
        return 'V'
      }

      return 'B'
    case 'PE':
    case 'PR':
      return 'V'
    default:
      return 'B'
  }
}


// -- for testing purposes only -----------------------------------------------------------

export const exportForTesting = {
  findPrice,
  findPricings,
  calcDisplayB,
  VERY_LARGE_NUMBER
}